import React, { useState, useEffect } from 'react';
import { IonIcon, IonButton, IonSpinner } from '@ionic/react';
import { refresh, heart, shareSocial, copy, checkmark } from 'ionicons/icons';
import { getDailyInspiration, refreshDailyInspiration, type DailyInspiration } from '../services/inspiration';
import './InspirationWidget.css';

// Fallback quotes when API/database is unavailable
const FALLBACK_QUOTES: DailyInspiration[] = [
  { quote_text: 'Believe you can and you\'re halfway there.', author: 'Theodore Roosevelt', source_name: 'Inspiration', background_color: null, background_image_url: null },
  { quote_text: 'The future belongs to those who believe in the beauty of their dreams.', author: 'Eleanor Roosevelt', source_name: 'Inspiration', background_color: null, background_image_url: null },
  { quote_text: 'It does not matter how slowly you go as long as you do not stop.', author: 'Confucius', source_name: 'Wisdom', background_color: null, background_image_url: null },
  { quote_text: 'Be the change you wish to see in the world.', author: 'Mahatma Gandhi', source_name: 'Wisdom', background_color: null, background_image_url: null },
  { quote_text: 'Happiness is not something ready made. It comes from your own actions.', author: 'Dalai Lama', source_name: 'Wisdom', background_color: null, background_image_url: null },
];

function getRandomFallbackQuote(): DailyInspiration {
  return FALLBACK_QUOTES[Math.floor(Math.random() * FALLBACK_QUOTES.length)];
}

interface InspirationWidgetProps {
  householdId: string;
  style?: 'card' | 'banner' | 'fullscreen' | 'minimal';
  showRefresh?: boolean;
  showShare?: boolean;
  className?: string;
}

const InspirationWidget: React.FC<InspirationWidgetProps> = ({
  householdId,
  style = 'card',
  showRefresh = true,
  showShare = true,
  className,
}) => {
  const [inspiration, setInspiration] = useState<DailyInspiration | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchInspiration = async () => {
      setLoading(true);
      try {
        const data = await getDailyInspiration(householdId);
        // Use fallback if no data returned
        setInspiration(data || getRandomFallbackQuote());
      } catch (error) {
        console.error('Failed to fetch inspiration:', error);
        setInspiration(getRandomFallbackQuote());
      }
      setLoading(false);
    };

    fetchInspiration();
  }, [householdId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const data = await refreshDailyInspiration(householdId);
      setInspiration(data || getRandomFallbackQuote());
    } catch (error) {
      console.error('Failed to refresh inspiration:', error);
      setInspiration(getRandomFallbackQuote());
    }
    setRefreshing(false);
  };

  const handleCopy = async () => {
    if (!inspiration) return;
    
    const text = inspiration.author 
      ? `"${inspiration.quote_text}" — ${inspiration.author}`
      : `"${inspiration.quote_text}"`;
    
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    if (!inspiration || !('share' in navigator)) return;

    try {
      await navigator.share({
        title: 'Daily Inspiration',
        text: inspiration.author 
          ? `"${inspiration.quote_text}" — ${inspiration.author}`
          : `"${inspiration.quote_text}"`,
      });
    } catch (error) {
      // User cancelled or share failed
      handleCopy(); // Fall back to copy
    }
  };

  if (loading) {
    return (
      <div className={`inspiration-widget ${style} loading ${className || ''}`}>
        <IonSpinner />
      </div>
    );
  }

  if (!inspiration) {
    return (
      <div className={`inspiration-widget ${style} empty ${className || ''}`}>
        <span>No inspiration today</span>
      </div>
    );
  }

  const backgroundStyle: React.CSSProperties = {};
  if (inspiration.background_image_url) {
    backgroundStyle.backgroundImage = `url(${inspiration.background_image_url})`;
  } else if (inspiration.background_color) {
    backgroundStyle.backgroundColor = inspiration.background_color;
  }

  return (
    <div 
      className={`inspiration-widget ${style} ${className || ''}`}
      style={backgroundStyle}
    >
      {inspiration.background_image_url && <div className="overlay" />}
      
      <div className="inspiration-content">
        <div className="quote-icon">💡</div>
        
        <blockquote className="quote-text">
          "{inspiration.quote_text}"
        </blockquote>
        
        {inspiration.author && (
          <cite className="quote-author">
            — {inspiration.author}
          </cite>
        )}
        
        <div className="quote-source">
          {inspiration.source_name}
        </div>
      </div>

      <div className="inspiration-actions">
        {showRefresh && (
          <IonButton 
            fill="clear" 
            size="small" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <IonIcon 
              icon={refresh} 
              className={refreshing ? 'spinning' : ''} 
            />
          </IonButton>
        )}
        
        {showShare && (
          <>
            <IonButton fill="clear" size="small" onClick={handleCopy}>
              <IonIcon icon={copied ? checkmark : copy} />
            </IonButton>
            
            {'share' in navigator && (
              <IonButton fill="clear" size="small" onClick={handleShare}>
                <IonIcon icon={shareSocial} />
              </IonButton>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Screensaver version - larger, more dramatic
export const InspirationScreensaver: React.FC<{
  householdId: string;
}> = ({ householdId }) => {
  const [inspiration, setInspiration] = useState<DailyInspiration | null>(null);

  useEffect(() => {
    getDailyInspiration(householdId).then(setInspiration);
  }, [householdId]);

  if (!inspiration) return null;

  return (
    <div className="inspiration-screensaver">
      <div className="screensaver-quote">
        <span className="quote-mark">"</span>
        <p className="quote-text">{inspiration.quote_text}</p>
        <span className="quote-mark closing">"</span>
      </div>
      {inspiration.author && (
        <p className="screensaver-author">— {inspiration.author}</p>
      )}
    </div>
  );
};

// Compact banner version
export const InspirationBanner: React.FC<{
  householdId: string;
  onClick?: () => void;
}> = ({ householdId, onClick }) => {
  const [inspiration, setInspiration] = useState<DailyInspiration | null>(null);

  useEffect(() => {
    getDailyInspiration(householdId).then(setInspiration);
  }, [householdId]);

  if (!inspiration) return null;

  // Truncate long quotes
  const maxLength = 100;
  const truncatedQuote = inspiration.quote_text.length > maxLength
    ? inspiration.quote_text.substring(0, maxLength) + '...'
    : inspiration.quote_text;

  return (
    <div className="inspiration-banner" onClick={onClick}>
      <span className="banner-icon">💡</span>
      <span className="banner-quote">"{truncatedQuote}"</span>
      {inspiration.author && (
        <span className="banner-author">— {inspiration.author}</span>
      )}
    </div>
  );
};

export default InspirationWidget;
